import { Geolocation } from '@capacitor/geolocation';
import type { LatLng } from './geo';
import { isNative } from './platform';

/** One position fix, asking for permission if needed. Throws with a message people can act on. */
export async function locateOnce(): Promise<LatLng> {
  try {
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 20_000, maximumAge: 60_000 });
    return { lat: pos.coords.latitude, lng: pos.coords.longitude };
  } catch (e) {
    const msg = String((e as { message?: string })?.message ?? '');
    const denied = (e as { code?: number })?.code === 1 || /denied|permission/i.test(msg);
    throw new Error(
      denied
        ? isNative
          ? 'Location is off for Backroad Stop. Turn it on in your phone’s settings, or type an address.'
          : 'Your browser blocked location. Allow it for this site, or type an address.'
        : 'Couldn’t find your location. Try again, or type an address.',
    );
  }
}

/** A fix only if location is already allowed: never shows a permission prompt. */
export async function locateQuietly(): Promise<LatLng | null> {
  try {
    if (isNative) {
      const perm = await Geolocation.checkPermissions();
      if (perm.location !== 'granted' && perm.coarseLocation !== 'granted') return null;
    } else {
      if (!navigator.permissions) return null;
      const p = await navigator.permissions.query({ name: 'geolocation' });
      if (p.state !== 'granted') return null;
    }
    return await locateOnce();
  } catch {
    return null;
  }
}
